<template>
  <template v-if="isHazmat">
    <h5 class="margin-top-s" v-text="hazmat.title"></h5>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <p class="margin-top-xs hazmat-battery-description" v-html="hazmat.text"></p>
  </template>
  <component :is="headingTag" id="return-instructions-title" :class="{ 'margin-top-s': !isReplaceExchange }">{{ instructionsTitle }}</component>
  <p v-if="isBorisReturn">
    These items will be shipped back to the seller. They’ll issue your refund within <span class="heavy">10 business days.</span>
  </p>
  <ol
    class="margin-left-xs margin-top-xs"
    :class="[
      {
        'print-label-instructions': isPrintLabel,
        'margin-bottom-m': isPrintLabel && !isHazmat,
        large: page === 'return-details',
      },
      isHazmat ? 'hazmat-instructions' : 'return-instructions',
    ]"
  >
    <component :is="instruction" v-for="instruction in instructions" :key="instruction" />
  </ol>
  <p v-if="!isPrintLabel && isReplaceExchange && page === 'return-details'" class="large ssre-note">
    *If your items are not dropped off by this date, your
    {{ returnResolution === 'EXCHANGE' ? 'exchange' : 'replacement' }} request will be canceled.
  </p>
  <p v-if="showBankRefundText" class="margin-top-xs" v-text="BANK_REFUND_TEXT"></p>
  <template v-if="isHazmat">
    <p id="hazmat-help" class="margin-top-xs" v-text="hazmat.help"></p>
    <p class="margin-top-xs"><strong aria-hidden="true">Note:</strong><span class="visibility-hidden">Note:</span> {{ hazmat.note }}</p>
  </template>
</template>

<script setup>
import { computed, h } from 'vue';
import {
  brand,
  returnInstructions,
  hazmatReturnInstructions,
  fedExHazmatReturnInstructions,
  HAZMAT_INSTRUCTIONS,
  BANK_REFUND_TEXT,
  RETURN_METHOD_KEY_MAP,
  REFUND_METHOD,
} from 'config';
import { formatDate } from '@/utils/General';

const props = defineProps({
  returnResolution: {
    type: String,
    default: '',
  },
  returnDropOffMethod: {
    type: String,
    required: true,
  },
  refundMethod: {
    type: String,
    required: false,
    default: '',
  },
  items: {
    type: Array,
    default: () => [],
  },
  makeUpsClickable: {
    type: Boolean,
    default: false,
  },
  uopFlow: {
    type: Boolean,
    default: false,
  },
  isMkpOrder: {
    type: Boolean,
    default: false,
  },
  isPrintLabel: {
    type: Boolean,
    default: false,
  },
  isBorisReturn: {
    type: Boolean,
    default: false,
  },
  isReplaceExchange: {
    type: Boolean,
    default: false,
  },
  dropOffDate: {
    type: String,
    default: '',
  },
  page: {
    type: String,
    default: '',
  },
  hasQrCodeUrl: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['upsClick']);
const instructionsTitle = computed(() => {
  let instructionType = `${props.returnDropOffMethod}-TITLE`;
  if (props.isReplaceExchange) instructionType = `${props.returnResolution}-TITLE`;
  else if (props.uopFlow) instructionType = 'uopTitle';
  return returnInstructions[instructionType];
});
const isHazmat = computed(() => {
  if (props.isBorisReturn) return false;
  return props.items.some((item) => item.isHazmatItem);
});

const showBankRefundText = computed(
  () => !props.isBorisReturn && props.returnDropOffMethod === RETURN_METHOD_KEY_MAP.storeReturn && props.refundMethod === REFUND_METHOD.ORIGINAL,
);

const hazmat = computed(() => {
  if (!isHazmat.value) return null;

  let helpText = props.isMkpOrder ? 'the seller' : 'customer service';
  if (brand === 'bcom') helpText = 'customer service at 1-800-777-0000';

  const { containedItem, standaloneItem, packedItem, title, note, help } = HAZMAT_INSTRUCTIONS;
  const hasContainedOrStandaloneItem = props.items.some((item) => [containedItem.key, standaloneItem.key].includes(item.hazmatDescription));

  let { text } = packedItem;
  if (hasContainedOrStandaloneItem) text = containedItem.text;

  return {
    title,
    text,
    help: help(helpText),
    note,
  };
});

const headingTag = computed(() => {
  if (props.isPrintLabel && !isHazmat.value) return 'h3';
  if (props.page === 'return-details') return 'h4';
  return 'h5';
});

const instructions = computed(() => {
  let instructionsArr = [];

  // Determine instruction type based on flow and hazmat status
  let instructionsType = isHazmat.value ? RETURN_METHOD_KEY_MAP.ups : props.returnDropOffMethod;
  if (props.isReplaceExchange && props.returnDropOffMethod !== RETURN_METHOD_KEY_MAP.labelFree) instructionsType = props.returnResolution;
  else if (props.isReplaceExchange && props.returnDropOffMethod === RETURN_METHOD_KEY_MAP.labelFree)
    instructionsType = `${props.returnResolution}-${props.returnDropOffMethod}`;

  instructionsArr = returnInstructions[instructionsType];

  // Handle Boris returns with special instructions
  if (props.isBorisReturn) {
    const instructionKey = props.hasQrCodeUrl ? `BORIS-QR-${props.returnDropOffMethod}` : `BORIS-${props.returnDropOffMethod}`;
    instructionsArr = returnInstructions[instructionKey];
  }

  // Handle BCOM hazmat with FedEx-specific instructions
  if (brand === 'bcom' && isHazmat.value) {
    instructionsArr = props.returnDropOffMethod === RETURN_METHOD_KEY_MAP.fedex ? fedExHazmatReturnInstructions : hazmatReturnInstructions;
  }

  let dropOffKey = '';
  if (props.isReplaceExchange) {
    if (props.returnDropOffMethod === RETURN_METHOD_KEY_MAP.labelFree) {
      dropOffKey = `${props.returnResolution}-${props.returnDropOffMethod}-DROP-OFF-TEXT`;
    } else {
      dropOffKey = `${props.returnResolution}-DROP-OFF-TEXT`;
    }
  } else {
    dropOffKey = `${props.returnDropOffMethod}-DROP-OFF-TEXT`;
  }
  const { text: upsText, cta: upsCta } = returnInstructions[dropOffKey] || {};

  /**
   * Renders replace/exchange instruction with formatted date
   */
  const renderReplaceExchange = (concatText = '') => {
    const completeInstructionWithDate = `${upsText + upsCta} ${concatText} ${formatDate(props.dropOffDate)}.${!props.isPrintLabel ? '*' : ''}`;

    if (!props.makeUpsClickable) {
      return h('li', {
        class: props.isPrintLabel && !isHazmat.value ? 'margin-left-m' : '',
        innerHTML: completeInstructionWithDate,
      });
    }

    return h('li', { role: 'listitem', 'aria-label': completeInstructionWithDate }, [
      h('span', { 'aria-hidden': true }, upsText),
      h(
        'button',
        {
          class: props.page === 'return-details' ? 'link-lg' : 'link-body',
          role: 'link',
          onClick: () => emit('upsClick'),
        },
        upsCta,
      ),
      h('span', { 'aria-hidden': true, class: '' }, ` ${concatText} `),
      h('span', { 'aria-hidden': true, class: 'heavy' }, `${formatDate(props.dropOffDate)}.`),
      ...(!props.isPrintLabel ? [h('span', {}, '*')] : []),
    ]);
  };

  return instructionsArr.map((instruction) => {
    const completeInstruction = `${upsText + upsCta} near you by XXX XX/XX.*`;

    // Handle replace/exchange instructions with dynamic date
    if (props.isReplaceExchange && instruction.includes(completeInstruction)) {
      return renderReplaceExchange('near you by');
    } else if (
      props.isReplaceExchange &&
      props.returnDropOffMethod === RETURN_METHOD_KEY_MAP.labelFree &&
      instruction.includes('Bring your package to The UPS Store')
    ) {
      return renderReplaceExchange('by');
    }

    // Make UPS CTA clickable when requested
    if (!props.isReplaceExchange && props.makeUpsClickable && instruction.includes(upsText + upsCta)) {
      return h('li', [
        h('span', { 'aria-hidden': true }, upsText),
        h('span', { class: 'visibility-hidden' }, upsText + upsCta),
        h(
          'button',
          {
            class: 'link-body',
            role: 'link',
            onClick: () => emit('upsClick'),
          },
          upsCta,
        ),
      ]);
    }

    // Default instruction rendering
    return h('li', {
      class: props.isPrintLabel && !isHazmat.value ? 'margin-left-m' : '',
      innerHTML: instruction,
    });
  });
});
</script>

<style lang="scss" scoped>
.print-label-instructions {
  li {
    line-height: rem-calc(24);
  }
}
.ssre-note {
  padding-top: rem-calc(20);
}
</style>
